import { useState } from 'react';
import PropTypes from 'prop-types';
import { Tab } from '@headlessui/react';
import toast from 'react-hot-toast';
import LargeModal from '../../components/Modal/LargeModal';
import ShipmentDetailForm from '../SampleReport/ShipmentDetailForm';
import IconTrash from '../../components/Icon/IconTrash';
import IconPen from "../../components/Icon/IconPen";
import { QUANTITY_CONSTANTS, SAMPLE_SIZE_CONSTANTS } from '../SampleReport/FormConstant';
import { usePostOrderSampleDetailEditMutation } from '../../store/services/api';

const EditOrderDetailModal = ({
                              open, onClose, data, isMetric, sampleData, updateFunction, updateSampleFunction, orderId, existingStatus, deleteSampleHandler, orderIndex
                          }) => {

    const [orderDetailData, setOrderDetailData] = useState(sampleData);
    const [editingSampleIndex, setEditingSampleIndex] = useState(null);
    const [editFields, setEditFields] = useState({ quantity: '', sampleSize: '' });
    const [editOrderSampleDetailTrigger] = usePostOrderSampleDetailEditMutation();

    const onCloseButtonClickHandler = () => {
        onClose();
    };

    const onSubmitHandler = async (formData) => {
        updateFunction(formData, orderId);
        onCloseButtonClickHandler();
    };

    const sampleDeleteHandle = (currentStatus, sampleIndex) => {
        if(orderDetailData.length === 1) {
            toast.error('You cannot delete only sample in the order please delete the order!');
        } else {
            setOrderDetailData((prevData) =>
                prevData.filter((_, index) => index !== sampleIndex)
            );
            deleteSampleHandler(currentStatus, orderIndex, sampleIndex);
            toast.success('Sample removed from order successfully!');
        }
    };

    const handleEditClick = (sampleIndex) => {
        setEditingSampleIndex(sampleIndex);
        setEditFields({
            quantity: orderDetailData[sampleIndex].quantity,
            sampleSize: orderDetailData[sampleIndex].sampleSize
        });
    };

    const handleEditFieldChange = (e) => {
        const { name, value } = e.target;
        setEditFields((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditSubmit = async (sampleIndex) => {
        const result = await editOrderSampleDetailTrigger({
            id: orderId,
            credentials: {
                index: sampleIndex,
                quantity: editFields.quantity,
                sampleSize: editFields.sampleSize
            }
        });

        if (result.data) {
            try {
                setOrderDetailData(prev =>
                    prev.map((item, idx) =>
                        idx === sampleIndex
                            ? { ...item, quantity: editFields.quantity, sampleSize: editFields.sampleSize }
                            : item
                    )
                );

                updateSampleFunction(sampleIndex, editFields, orderId);
                toast.success('Edit Successful!');
            } catch (error) {
                toast.error('Edit Failed!');
                return;
            }
        } else {
            toast.error('Edit Failed!');
        }

        setEditingSampleIndex(null);
    };

    const handleEditCancel = () => {
        setEditingSampleIndex(null);
    };

    return (
        <LargeModal open={open} setOpen={onCloseButtonClickHandler}>
            <div>
                <Tab.Group>
                    <Tab.Panels>
                        <h2 className="font-bold pb-4">Order Details</h2>
                        <div className="flex flex-row w-full text-white text-sm pt-4 h-16">
                            <div className="w-[10%] flex justify-center items-center bg-[#0d3857] border-solid border-r-2 border-white group relative cursor-pointer">
                                <h2 className="text-center">Glass Type</h2>
                            </div>
                            <div className="w-[10%] flex justify-center items-center bg-[#0d3857] border-solid border-r-2 border-white group relative cursor-pointer">
                                <h2 className="text-center">Performance</h2>
                            </div>
                            <div className="w-[10%] flex justify-center items-center bg-[#0d3857] border-solid border-r-2 border-white group relative cursor-pointer">
                                <h2 className="text-center">Drawing</h2>
                            </div>
                            <div className="w-[30%] flex justify-center items-center bg-[#0d3857] border-solid border-r-2 border-white group relative cursor-pointer">
                                <h2 className="text-center">Product</h2>
                            </div>
                            <div className="w-[10%] flex justify-center items-center bg-[#0d3857] border-solid border-r-2 border-white group relative cursor-pointer">
                                <h2 className="text-center">Note</h2>
                            </div>
                            <div className="w-[15%] flex justify-center items-center bg-[#0d3857] border-solid border-r-2 border-white group relative cursor-pointer">
                                <h2 className="text-center">Quantity & Sample Size</h2>
                            </div>
                            <div className="w-[15%] flex justify-center items-center bg-[#0d3857] border-solid border-r-2 border-white group relative cursor-pointer">
                                <h2 className="text-center">Action</h2>
                            </div>
                        </div>
                        {orderDetailData.map((item, sampleIndex) => (
                            <div className="flex flex-row w-full h-auto text-xs">
                                <div className="w-[10%] flex justify-center items-center bg-[#f3f6f7] border-solid border-r-2 border-b-2 border-white p-2">
                                    <h2 className="text-center">{item.family}</h2>
                                </div>
                                <div className="w-[10%] flex justify-center items-center bg-[#f3f6f7] border-solid border-r-2 border-b-2 border-white p-2">
                                    <div className="flex flex-col">
                                        <h2>VLT: {item.description_visible}</h2>
                                        <h2>LSG: {item.light_solar_gain_ration}</h2>
                                        <h2>SC: {item.shading_coefficient}</h2>
                                        <h2>SHGC: {item.solar_heat_gain_coefficient}</h2>
                                        <h2>Summer U: {isMetric ? item.u_value_summer_metric : item.u_value_summer}</h2>
                                        <h2>Winter U: {isMetric ? item.u_value_solar_metric : item.u_value_solar}</h2>
                                        <h2>R Out: {item.reflectance_out}</h2>
                                    </div>
                                </div>
                                <div className="w-[10%] flex justify-center items-center bg-[#f3f6f7] border-solid border-r-2 border-b-2 border-white p-2">
                                    <h2 className="text-center">{item.drawing}</h2>
                                </div>
                                <div className="w-[30%] flex justify-center items-center bg-[#f3f6f7] border-solid border-r-2 border-b-2 border-white whitespace-pre-line p-2">
                                    <h2 className="text-center">
                                        {isMetric && (
                                            <>
                                                <div>{item.top_line_metric}</div>
                                                {item.spec_metric.map((line, index) => (
                                                    <div>{line}</div>
                                                ))}
                                            </>
                                        )}
                                        {!isMetric && (
                                            <>
                                                <div>{item.top_line_imp}</div>
                                                {item.spec_imp.map((line, index) => (
                                                    <div>{line}</div>
                                                ))}
                                            </>
                                        )}
                                    </h2>
                                </div>
                                <div className="w-[10%] flex justify-center items-center bg-[#f3f6f7] border-solid border-r-2 border-b-2 border-white p-2">
                                    <h2 className="text-center">{item.note}</h2>
                                </div>
                                <div className="w-[15%] flex justify-center items-center bg-[#f3f6f7] border-solid border-r-2 border-b-2 border-white p-2">
                                    {editingSampleIndex === sampleIndex ? (
                                        <div className="flex flex-col items-center w-full">
                                            <select
                                                id="quantitySize"
                                                    name="quantity"
                                                    className="flex flex-col w-3/4 rounded border border-gray-300 py-2"
                                                    onChange={(event) => handleEditFieldChange(event)}
                                                    value={editFields.quantity}
                                            >
                                                <option selected disabled>Select Quantity</option>
                                                {QUANTITY_CONSTANTS.map((quantitySize) => (
                                                    <option value={quantitySize.id}>{quantitySize.value}</option>
                                                ))}
                                            </select>
                                            <select
                                                id="sampleSize"
                                                name="sampleSize"
                                                value={editFields.sampleSize}
                                                className="flex flex-col w-3/4 rounded border border-gray-300 py-2"
                                                onChange={(event) => handleEditFieldChange(event)}
                                            >
                                                {/* <option selected>Select Sample Size</option> */}
                                                {SAMPLE_SIZE_CONSTANTS.map((sampleSize) => (
                                                    <option value={sampleSize.value}>{sampleSize.value}</option>
                                                ))}
                                            </select>
                                            {/* <input
                                                type="number"
                                                name="quantity"
                                                value={editFields.quantity}
                                                onChange={handleEditFieldChange}
                                                className="border rounded px-1 py-0.5 w-16 mb-1 text-center"
                                            />
                                            <input
                                                type="text"
                                                name="sampleSize"
                                                value={editFields.sampleSize}
                                                onChange={handleEditFieldChange}
                                                className="border rounded px-1 py-0.5 w-16 text-center"
                                            /> */}
                                        </div>
                                    ) : (
                                        <>
                                            <h2 className="text-center">{item.quantity}x</h2>
                                            <h2 className="text-center pl-2">{item.sampleSize}</h2>
                                        </>
                                    )}
                                </div>
                                <div className="w-[15%] flex justify-center items-center bg-[#f3f6f7] border-solid border-r-2 border-b-2 border-white p-2">
                                    {editingSampleIndex === sampleIndex ? (
                                        <div className="flex flex-row gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handleEditSubmit(sampleIndex)}
                                                className="bg-green-500 text-white px-2 py-1 rounded"
                                            >
                                                Submit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleEditCancel}
                                                className="bg-gray-400 text-white px-2 py-1 rounded"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        /* eslint-disable-next-line react/jsx-no-useless-fragment */
                                        <>
                                            {existingStatus === 'Pending' && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditClick(sampleIndex)}
                                                        className="p-2 group relative cursor-pointer"
                                                    >
                                                        <IconPen />
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        onClick={() => sampleDeleteHandle(existingStatus, sampleIndex)}
                                                        className="p-2 group relative cursor-pointer"
                                                        aria-label="Sample Delete"
                                                    >
                                                        <IconTrash />
                                                    </button>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </Tab.Panels>
                </Tab.Group>
            </div>
            <div>
                <Tab.Group>
                    <Tab.Panels>
                        <ShipmentDetailForm data={[]} prevFormData={data} submitFunction={onSubmitHandler} buttonLabel="Update Order"/>
                    </Tab.Panels>
                </Tab.Group>
            </div>
        </LargeModal>
    );
};

EditOrderDetailModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    data: PropTypes.object.isRequired,
    isMetric: PropTypes.bool.isRequired,
    sampleData: PropTypes.object.isRequired,
    updateFunction: PropTypes.func.isRequired,
    updateSampleFunction: PropTypes.func.isRequired,
    orderId: PropTypes.string.isRequired,
    existingStatus: PropTypes.string.isRequired,
    deleteSampleHandler: PropTypes.func.isRequired,
    orderIndex: PropTypes.number.isRequired,
};

export default EditOrderDetailModal;